import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import {
  ASSIGNED_SEATING_MODES,
  common,
  enums,
  PAYMENT_TYPE_OPTIONS_MAP,
} from "@shared/constants";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { waitForElementToLoad } from "@shared/services/loaders/element-loader";
import { useEffect, useMemo, useState } from "react";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useSeatingState } from "../../contexts/useSeatingState";
import SeatingMapService, {
  TooltipParamType,
  TooltipReturnType,
} from "../../services/SeatingMapService";
import { SEATING_ITEM_TYPE_MAP } from "../../types/seating-property-maps";
import { SeatingItemPropertyMap } from "../../types/seating-property-types";
import { PublicBaseSpaceSerializer } from "../../types/seating-types";
import { BasicSeatingMapSharedProps } from "./BasicSeatingMap.props";

export function useBasicSeatingMap(props: BasicSeatingMapSharedProps) {
  const {
    venue,
    spaceData,
    mapItemType,
    currentPackageOption,
    mapMode,
    showOtherEvents,
    exchangeSeatPermissions,
  } = props;

  const isPackageFlow = currentPackageOption !== undefined;
  const { isEmbedded } = useEmbeddedComponent();

  // get the basket type from the map mode
  const isVenueBasket =
    mapMode === ASSIGNED_SEATING_MODES.EVENT_BOX_OFFICE_VIEW.id;
  const basketType = isVenueBasket
    ? common.DataProvider.Venue
    : common.DataProvider.Public;
  const { itemGroups } = useBasketService();

  const {
    isLoadingMap, // displays loading spinner when loading or reloading the map
    setIsLoadingMap,
    updateSeatingState,
    stageServiceState,
    seatingServiceState,
    seatingEventData,
    seatingItemArray,
  } = useSeatingState();

  const [seatingMapService] = useState(
    () => new SeatingMapService(stageServiceState, seatingServiceState)
  );
  const [space, setSpace] = useState<PublicBaseSpaceSerializer | undefined>();

  const canvasId = "canvas-seating-map";
  const allInPricing = Boolean(venue.all_in_pricing_is_enabled);

  const [windowResizeFunc, setWindowResizeFunc] =
    useState<EventListenerOrEventListenerObject>();

  const [advancedSeatColors, setAdvancedSeatColors] = useState(false);
  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 10, y: 10 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipInfo, setTooltipInfo] = useState<TooltipReturnType>();

  const { isFlagActive } = useDjangoWaffle();

  /**
   * Handle mouse move on the canvas.
   * This is used to control the tooltip functionality.
   */
  const handleMouseMove = ({
    data,
    mouseX,
    mouseY,
    hidden,
  }: {
    data: TooltipParamType;
    mouseX: number;
    mouseY: number;
    hidden: boolean;
  }) => {
    if (hidden) {
      // hide tooltip
      setShowTooltip(false);
    } else {
      const {
        details,
        itemTypes,
        isAccessible,
        isLimitedVisibility,
        isResale,
      }: TooltipReturnType = seatingMapService.setTooltipContent(data);

      // set tooltip settings
      setTooltipInfo({
        details,
        isAccessible,
        isLimitedVisibility,
        isResale,
        itemTypes,
      });
      setMousePosition({ x: mouseX, y: mouseY });
      setShowTooltip(Boolean(details));
    }
  };

  /**
   * Displays a message on the seating map that seats will be visible by clicking on a section
   */
  const displaySeatingMessage = () => {
    // checks if the map has loaded, plus seat threshold conditions
    // (different thresholds for Android vs other devices)
    return (
      !isLoadingMap &&
      stageServiceState &&
      (stageServiceState.shouldNotDisplayAllSeats() ||
        !space?.always_display_seats_and_locations) &&
      !isFlagActive("enable_best_assigned_seating", venue.id)
    );
  };

  const initSeatingMap = (overrideItemArray: Array<SeatingItemPropertyMap>) => {
    // set event and space ids
    seatingServiceState.seatingObjectId =
      spaceData.event ?? spaceData.membership_benefit;
    seatingServiceState.spaceId = seatingEventData?.assigned_space;
    seatingServiceState.venueId = venue.id;

    // set the map mode
    seatingServiceState.mode = mapMode;

    // set event sold out segments
    seatingServiceState.soldOutSegments =
      seatingEventData?.sold_out_segments ?? [];

    // set the basket type
    seatingServiceState.basketType = basketType;

    seatingServiceState.mapItemType = mapItemType;

    seatingServiceState.seatingObjectPropertyMap =
      SEATING_ITEM_TYPE_MAP[mapItemType].propertyMap;

    seatingServiceState.itemArrayPropertyMap =
      SEATING_ITEM_TYPE_MAP[mapItemType].nestedItemType.propertyMap;

    seatingServiceState.seatPermissionPropertyMap =
      SEATING_ITEM_TYPE_MAP[mapItemType].seatPermissionType.propertyMap;

    seatingServiceState.locationPermissionPropertyMap =
      SEATING_ITEM_TYPE_MAP[mapItemType].locationPermissionType.propertyMap;

    seatingServiceState.exchangeSeatPermissions = exchangeSeatPermissions;

    // fetch and set event space
    seatingServiceState
      .fetchSpace(seatingEventData?.assigned_space)
      .then((res: PublicBaseSpaceSerializer) => {
        const fetchedSpace = res;

        setSpace(fetchedSpace);
        setAdvancedSeatColors(fetchedSpace.display_advanced_seat_colors);
        seatingServiceState.advancedSeatColors =
          fetchedSpace.display_advanced_seat_colors;

        const fetchPermissionParams = {
          event: spaceData.event,
          membership_benefit: spaceData.membership_benefit,
          basketHoldID: seatingServiceState.basketHoldID,
          exchangeSeatPermissions: Array.isArray(exchangeSeatPermissions)
            ? exchangeSeatPermissions.filter((p) => p !== null).join(",")
            : exchangeSeatPermissions,
        };

        // wait for permissions to be initialized first
        seatingServiceState
          .initPermissions(overrideItemArray, fetchPermissionParams)
          .then(() => {
            // initialize basket
            seatingServiceState.initBasket(itemGroups, seatingEventData?.id);

            // initialize map display
            waitForElementToLoad(`#${canvasId}`).then(async () => {
              const element = SeatingMapService.setCanvasContainer(canvasId);
              await stageServiceState.init(`#${canvasId}`, fetchedSpace, {
                width: element?.offsetWidth ?? 0,
                height: element?.offsetHeight ?? 0,
                handleComplete: () => {
                  stageServiceState.centerStage();
                  seatingMapService.handleWindowResize(element);

                  // sets the cart items if the page is refreshed
                  updateSeatingState();

                  // indicates map is finished loading
                  setIsLoadingMap(false);
                  setIsInitialPageLoad(false);
                },
              });

              let handleResizeDelay;

              // Resizes the map element when the browser is resized.
              // Re-centers the stage and resets the amount of zoom.
              function windowResizeCallback() {
                clearTimeout(handleResizeDelay);
                handleResizeDelay = setTimeout(
                  seatingMapService.handleWindowResize(element),
                  100
                );
              }
              // allow useeffect cleanups to access this function
              setWindowResizeFunc(windowResizeCallback);

              // add delay to function call so that we get the size of element at the end of the resize event
              window.addEventListener("resize", windowResizeCallback);
              stageServiceState.setMouseMove(handleMouseMove);
            });
          });
      });
  };

  useEffect(() => {
    if (isInitialPageLoad && seatingItemArray) {
      initSeatingMap(seatingItemArray);
    }

    // clean up useeffect
    return () =>
      window.removeEventListener(
        "resize",
        windowResizeFunc as EventListenerOrEventListenerObject
      );
    // this useEffect should only run once on page load
  }, []); // eslint-disable-line

  // this useEffect should run when the page is loaded but a completely new map needs to be displayed
  // example 1: when a new package option is clicked on, a new map page is displayed
  useEffect(() => {
    if (!isInitialPageLoad) {
      setIsLoadingMap(true);

      // reset pending permissions when navigating to a new seating map page
      if (seatingServiceState) {
        seatingServiceState.pendingCartPermissions = [];
      }

      initSeatingMap(seatingItemArray ?? []);
    }

    // clean up useeffect
    return () => {
      window.removeEventListener(
        "resize",
        windowResizeFunc as EventListenerOrEventListenerObject
      );
      setIsLoadingMap(true); // reset loading state when component unmounts - for attendance mode
    };
    // watch for a new event selection
  }, [currentPackageOption]); // eslint-disable-line

  // this useEffect should run when the page is loaded and only the stage needs to be refreshed
  // example: when a minimum purchase item is clicked on, the same map reloads to show a single item type
  useEffect(() => {
    const showMapWithAllItems =
      !isInitialPageLoad &&
      seatingServiceState &&
      !stageServiceState?.isLimitedSeatSelectionEnabled &&
      !seatingServiceState.overridePurchaseLimits?.multiItemsAllowed; // don't refresh map if multiple items can be added to the pending cart

    const showMapWithMinPurchaseItems =
      !isInitialPageLoad &&
      seatingServiceState &&
      stageServiceState?.isLimitedSeatSelectionEnabled &&
      !seatingServiceState.overridePurchaseLimits?.multiItemsAllowed; // don't refresh map if multiple items can be added to the pending cart

    if (showMapWithAllItems) {
      // get all of the item type ids on the map
      const allItemTypeIds = seatingItemArray?.map((tt) => tt.id) ?? [];
      stageServiceState?.enableGAByItemType(true, allItemTypeIds);
      stageServiceState?.enableSeatsByItemType(allItemTypeIds);
    } else if (showMapWithMinPurchaseItems) {
      // get the selected item type id on the map
      const selectedItemTypeId = seatingItemArray?.find(
        (tt) =>
          tt.id === seatingServiceState?.pendingCartPermissions[0].itemTypeId
      )?.id;

      if (selectedItemTypeId) {
        stageServiceState?.enableGAByItemType(false, [selectedItemTypeId]);
        stageServiceState?.enableSeatsByItemType([selectedItemTypeId]);
      }
    }
    // watch for changes in pending cart permissions
  }, [stageServiceState?.isLimitedSeatSelectionEnabled]); // eslint-disable-line

  const eventParams = {
    purchase_platform: isEmbedded
      ? getEmbeddedPurchasePlatform()
      : enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
    ].toString(),
  };

  // fetch parent event data for recurring assigned seating events
  // only when on a public map and there is no hold id
  const { useRetrieve } = usePublicEventsService();
  const { data: parentEventData } = useRetrieve(
    Number(seatingEventData?.parent_event),
    {
      config: {
        params: eventParams,
      },
      options: {
        keepPreviousData: true, // cache data
        enabled: Boolean(
          seatingEventData?.parent_event &&
            showOtherEvents &&
            mapMode === ASSIGNED_SEATING_MODES.EVENT_DETAIL_VIEW.id
        ),
      },
    }
  );

  const showTimeslotDrawer = useMemo(
    () =>
      Boolean(
        showOtherEvents &&
          !isEmbedded &&
          mapMode === ASSIGNED_SEATING_MODES.EVENT_DETAIL_VIEW.id &&
          (parentEventData?.child_events?.length ?? 0)
      ),
    [showOtherEvents, mapMode, parentEventData]
  );

  return {
    displaySeatingMessage,
    tooltipInfo,
    mousePosition,
    showTooltip,
    canvasId,
    allInPricing,
    advancedSeatColors,
    parentEventData,
    showTimeslotDrawer,
    isPackageFlow,
  };
}
